/**
 * Loan risk & eligibility — reusable pure helpers for user + admin.
 * EMI here matches app logic: flat total (principal + interest + fees) / tenure.
 */

/** @typedef {'LOW'|'MEDIUM'|'HIGH'} RiskLevel */
/** @typedef {'APPROVE'|'REVIEW'|'REJECT'} Decision */

/**
 * Monthly installment when total repayable is spread evenly (current product model).
 */
export function calculateEmi(totalPayable, durationMonths) {
  const n = Math.max(1, Math.floor(Number(durationMonths) || 1));
  const total = Number(totalPayable) || 0;
  return total / n;
}

/**
 * DTI = (existing monthly debt + new loan EMI) / monthly income * 100
 */
export function calculateDti(monthlyIncome, existingMonthlyDebt, emi) {
  const inc = Number(monthlyIncome) || 0;
  if (inc <= 0) return 100;
  const debt = Math.max(0, Number(existingMonthlyDebt) || 0);
  const e = Math.max(0, Number(emi) || 0);
  return ((debt + e) / inc) * 100;
}

export function emiToIncomePercent(monthlyIncome, emi) {
  const inc = Number(monthlyIncome) || 0;
  if (inc <= 0) return 100;
  return (Number(emi) || 0) / inc * 100;
}

/**
 * Eligibility caps from income (conservative ×2, stretch ×3).
 */
export function maxEligibleLoanAmounts(monthlyIncome) {
  const inc = Math.max(0, Number(monthlyIncome) || 0);
  return {
    maxLoanConservative: inc * 2,
    maxLoanStretch: inc * 3,
  };
}

/**
 * Risk score 0–100 from income band, DTI, EMI burden.
 */
export function calculateRiskScore(monthlyIncome, dtiPercent, emiIncomePercent) {
  const inc = Number(monthlyIncome) || 0;
  let score = 100;

  // Income stability band (LKR-oriented tiers; adjust if product is multi-currency)
  if (inc > 0 && inc < 40000) score -= 15;
  else if (inc >= 40000 && inc < 80000) score -= 8;
  else if (inc >= 80000 && inc < 150000) score -= 3;

  // DTI penalty
  score -= Math.min(45, (Number(dtiPercent) || 0) * 0.65);

  // EMI vs income (above ~15% starts to bite)
  const emiPct = Number(emiIncomePercent) || 0;
  score -= Math.min(35, Math.max(0, emiPct - 12) * 1.2);

  return Math.max(0, Math.min(100, Math.round(score)));
}

/** Map numeric score to LOW / MEDIUM / HIGH */
export function riskLevelFromScore(riskScore) {
  const s = Number(riskScore) || 0;
  if (s >= 80) return 'LOW';
  if (s >= 50) return 'MEDIUM';
  return 'HIGH';
}

/**
 * Rules:
 * - EMI > 40% of income → HIGH RISK → REJECT
 * - DTI > 50% → HIGH RISK → REJECT
 * - DTI 30–50% → MEDIUM → REVIEW (if not rejected above)
 * - DTI < 30% → LOW → APPROVE (if not rejected above)
 */
export function decisionFromRules(emiIncomePercent, dtiPercent) {
  const emiPct = Number(emiIncomePercent) || 0;
  const dti = Number(dtiPercent) || 0;

  if (emiPct > 40 || dti > 50) {
    return { decision: /** @type {Decision} */ ('REJECT'), ruleRisk: /** @type {RiskLevel} */ ('HIGH') };
  }
  if (dti >= 30 && dti <= 50) {
    return { decision: /** @type {Decision} */ ('REVIEW'), ruleRisk: /** @type {RiskLevel} */ ('MEDIUM') };
  }
  return { decision: /** @type {Decision} */ ('APPROVE'), ruleRisk: /** @type {RiskLevel} */ ('LOW') };
}

/**
 * Suggest a lower principal so EMI and DTI stay within caps (binary search).
 * totalPayableFn(principal) must return total repayable for that principal.
 */
export function suggestAffordablePrincipal({
  monthlyIncome,
  existingMonthlyDebt,
  durationMonths,
  requestedPrincipal,
  totalPayableFn,
  maxIterations = 24,
}) {
  const income = Number(monthlyIncome) || 0;
  const debt = Math.max(0, Number(existingMonthlyDebt) || 0);
  const duration = Math.max(1, Math.floor(Number(durationMonths) || 1));
  const requested = Math.max(0, Number(requestedPrincipal) || 0);
  if (income <= 0 || requested <= 0) return null;

  const passes = (principal) => {
    const tp = totalPayableFn(principal);
    if (tp == null || !Number.isFinite(tp) || tp <= 0) return false;
    const emi = calculateEmi(tp, duration);
    const dti = calculateDti(income, debt, emi);
    const emiPct = emiToIncomePercent(income, emi);
    return emiPct <= 40 && dti <= 50;
  };

  if (passes(requested)) return Math.round(requested);

  let low = 0;
  let high = requested;
  let best = null;
  for (let i = 0; i < maxIterations && high - low > 1; i++) {
    const mid = (low + high) / 2;
    if (passes(mid)) {
      best = mid;
      low = mid;
    } else {
      high = mid;
    }
  }
  if (best != null) return Math.max(0, Math.round(best));
  // try tiny amount
  for (const p of [1000, 5000, 10000, 20000]) {
    if (p < requested && passes(p)) return p;
  }
  return null;
}

/**
 * Full structured output for Firestore + admin UI.
 */
export function buildLoanEligibilityAssessment({
  monthlyIncome,
  existingMonthlyDebt,
  requestedLoan,
  durationMonths,
  totalPayable,
}) {
  const income = Number(monthlyIncome) || 0;
  const debt = Math.max(0, Number(existingMonthlyDebt) || 0);
  const requested = Number(requestedLoan) || 0;
  const duration = Math.max(1, Math.floor(Number(durationMonths) || 1));
  const total = Number(totalPayable) || 0;

  const emi = calculateEmi(total, duration);
  const dti = calculateDti(income, debt, emi);
  const emiIncomePct = emiToIncomePercent(income, emi);

  const { maxLoanConservative, maxLoanStretch } = maxEligibleLoanAmounts(income);
  const riskScore = calculateRiskScore(income, dti, emiIncomePct);
  const riskLevel = riskLevelFromScore(riskScore);
  const { decision, ruleRisk } = decisionFromRules(emiIncomePct, dti);

  let suggestedLoanAmount = requested;
  if (requested > maxLoanConservative) {
    suggestedLoanAmount = Math.min(requested, maxLoanConservative);
  }

  const flaggedRiskyUser = decision === 'REJECT' || riskLevel === 'HIGH' || ruleRisk === 'HIGH';

  return {
    income,
    existingMonthlyDebt: debt,
    requestedLoan: requested,
    durationMonths: duration,
    totalPayable: total,
    EMI: Math.round(emi * 100) / 100,
    DTI: Math.round(dti * 100) / 100,
    emiIncomePercent: Math.round(emiIncomePct * 100) / 100,
    riskScore,
    riskLevel,
    ruleRisk,
    decision,
    maxLoanConservative,
    maxLoanStretch,
    suggestedLoanAmount: Math.round(suggestedLoanAmount),
    exceedsIncomeMultiple: requested > maxLoanConservative,
    flaggedRiskyUser,
    computedAt: new Date().toISOString(),
  };
}

/**
 * Recompute suggestion after rejection using proportional totalPayable (flat fee/interest on principal).
 * If your product uses non-linear totalPayable, pass a custom totalPayableFn from the caller.
 */
export function enrichWithAdjustedSuggestion(assessment, totalPayableForPrincipal) {
  if (typeof totalPayableForPrincipal !== 'function') return assessment;

  const income = assessment.income;
  const debt = assessment.existingMonthlyDebt;
  const duration = assessment.durationMonths;
  const requested = assessment.requestedLoan;

  if (assessment.decision !== 'REJECT') {
    return { ...assessment, adjustedPrincipalSuggestion: null };
  }

  const adjusted = suggestAffordablePrincipal({
    monthlyIncome: income,
    existingMonthlyDebt: debt,
    durationMonths: duration,
    requestedPrincipal: requested,
    totalPayableFn: totalPayableForPrincipal,
  });

  let adjustedTotalPayable = null;
  let adjustedEMI = null;
  let adjustedDTI = null;
  if (adjusted != null && adjusted > 0) {
    adjustedTotalPayable = totalPayableForPrincipal(adjusted);
    adjustedEMI = calculateEmi(adjustedTotalPayable, duration);
    adjustedDTI = calculateDti(income, debt, adjustedEMI);
  }

  return {
    ...assessment,
    adjustedPrincipalSuggestion: adjusted,
    adjustedTotalPayable: adjustedTotalPayable != null ? Math.round(adjustedTotalPayable * 100) / 100 : null,
    adjustedEMI: adjustedEMI != null ? Math.round(adjustedEMI * 100) / 100 : null,
    adjustedDTI: adjustedDTI != null ? Math.round(adjustedDTI * 100) / 100 : null,
  };
}

/** Extract monthly debt from loan application details (backward compatible). */
export function getExistingMonthlyDebtFromDetails(details) {
  if (!details?.financial) return 0;
  const raw = details.financial.monthlyDebt ?? details.financial.existingMonthlyDebt;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Build assessment from a stored loan document (admin / legacy).
 */
export function eligibilityFromLoanDocument(loan) {
  if (!loan) return null;
  const details = loan.details;
  if (!details) return null;

  const income = Number(details.employment?.monthlyIncome) || 0;
  const debt = getExistingMonthlyDebtFromDetails(details);
  const requested = Number(loan.amountRequested) || 0;
  const duration = Math.max(1, Math.floor(Number(details.loan?.duration) || 1));
  const total = Number(loan.totalPayable) || 0;

  return buildLoanEligibilityAssessment({
    monthlyIncome: income,
    existingMonthlyDebt: debt,
    requestedLoan: requested,
    durationMonths: duration,
    totalPayable: total,
  });
}

/**
 * Add adjusted-principal suggestion when viewing in admin (matches stored loan pricing).
 */
export function enrichAssessmentForLoanDocument(loan, assessment) {
  if (!assessment || !loan?.details?.loan) return assessment;
  const detailsLoan = loan.details.loan;
  const rate = Number(loan.interestRate ?? detailsLoan.interestRate ?? 0);
  const rateType = loan.rateType || detailsLoan.rateType || 'monthly';
  const serviceFeePct = Number(loan.serviceFee ?? 0);
  const duration = Math.max(1, Math.floor(Number(detailsLoan.duration) || 1));

  const interestOnPrincipal = (amt) => {
    const p = Number(amt) || 0;
    if (!rate) return 0;
    if (rateType === 'annual') return p * (rate / 100) * (duration / 12);
    if (rateType === 'monthly') return p * (rate / 100) * duration;
    return 0;
  };

  const totalPayableForPrincipal = (p) => {
    const principal = Number(p) || 0;
    return principal + interestOnPrincipal(principal) + principal * (serviceFeePct / 100);
  };

  return enrichWithAdjustedSuggestion(assessment, totalPayableForPrincipal);
}
